import type { ObjectType, PermissionLevel } from '@/shared/types/permission';
import { useCheckObjectPermissionQuery } from '@/features/permissions/permissionsApi';

export interface ObjectPermissionState {
  isAllowed: boolean;
  isDenied: boolean;
  isIndeterminate: boolean;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

interface UseObjectPermissionStateOptions {
  fallbackAllowed?: boolean;
}

export function useObjectPermissionState(
  objectType: ObjectType,
  objectId: string | undefined,
  min: PermissionLevel = 'editor',
  options?: UseObjectPermissionStateOptions
): ObjectPermissionState {
  const fallbackAllowed = options?.fallbackAllowed ?? false;
  const shouldSkipQuery = !objectId || fallbackAllowed;
  const { data, isLoading, isFetching, isError, refetch } = useCheckObjectPermissionQuery(
    { object_type: objectType, object_id: objectId!, min },
    {
      skip: shouldSkipQuery,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  if (!objectId) {
    return {
      isAllowed: false,
      isDenied: false,
      isIndeterminate: false,
      isLoading: false,
      isError: false,
      retry: () => {},
    };
  }

  if (fallbackAllowed) {
    return {
      isAllowed: true,
      isDenied: false,
      isIndeterminate: false,
      isLoading: false,
      isError: false,
      retry: () => {},
    };
  }

  const isAllowed = data === true;
  const isIndeterminate = isLoading || isFetching || (isError && data === undefined);

  return {
    isAllowed,
    isDenied: !isAllowed && !isIndeterminate,
    isIndeterminate,
    isLoading: isLoading || isFetching,
    isError,
    retry: () => {
      void refetch();
    },
  };
}
